'use client';

import { useState } from 'react';

import Link from 'next/link';

import { CornerUpLeft, Loader2, Pin, RotateCcw } from 'lucide-react';

import { useCommunityMessageContext } from '../../contexts/community-message/useCommunityMessageContext';
import { useCommunityContext } from '../../contexts/community/useCommunityContext';
import { formatChannelAuthorName } from '../../functions/utils/formatChannelAuthorName';
import {
  formatChannelTimestamp,
  formatMessageTimestamp,
} from '../../functions/utils/formatChannelTimestamps';
import { useLiveMessage } from '../../hooks/useLiveMessage';
import { useMyChannelMembership } from '../../hooks/useMyChannelMembership';
import { useMyMessageBookmarks } from '../../hooks/useMyMessageBookmarks';
import type { ExtendedCommunityChannelMessage } from '../../types/ExtendedCommunityChannelMessage';
import type { MessageInteractionMode } from '../../types/MessageInteractionMode';
import { ChannelMessageMenu } from '../channel-message-view/ChannelMessageMenu';
import { MessageBookmarkIndicator } from '../channel-message-view/MessageBookmarkIndicator';
import { MessageContentRenderer } from '../channel-message-view/MessageContentRenderer';
import { MessageReactionBar } from '../channel-message-view/MessageReactionBar';
import { MessageSubscriptionIndicator } from '../channel-message-view/MessageSubscriptionIndicator';
import { ChannelInlineReplies } from './ChannelInlineReplies';
import { MessageAuthorCard } from './MessageAuthorCard';
import { ChannelMessageAttachments } from './attachments/ChannelMessageAttachments';
import { ChannelMessageLinks } from './links/ChannelMessageLinks';

interface Props {
  message: ExtendedCommunityChannelMessage;
  /**
   * Used to render the "(You)" label next to the author name and to
   * scope the "did I react?" highlight on reaction chips.
   */
  currentUserId: string | null | undefined;
  /**
   * Channel slug, threaded into the View Replies / Reply links so
   * they navigate to the right thread URL. When omitted the
   * thread-navigation links are inert (used when the bubble renders
   * inside the thread pane itself).
   */
  channelSlug?: string;
  /**
   * Pass `true` to suppress the "Reply / View Replies" links — used
   * by the thread pane so we don't recursively re-open threads
   * from a reply.
   */
  hideThreadLinks?: boolean;
  /**
   * How the reply affordances behave:
   * - `navigate` (default): "View Replies" / "Reply" are links to the thread
   *   route (main list, pinned pane, thread pane).
   * - `inline`: they expand the replies INLINE beneath the message via
   *   {@link ChannelInlineReplies} — for sidebar surfaces (bookmarks / reports)
   *   where a thread pane can't open. Replies-to-replies expand recursively.
   */
  repliesMode?: 'navigate' | 'inline';
  /**
   * Keep the message visible as a "deleted" placeholder if its underlying doc
   * is gone, instead of hiding it. Used by the bookmarks panel, which renders
   * from its own stored snapshot (upstream's `gone` behaviour).
   */
  preserveWhenGone?: boolean;
  /**
   * The viewer's interaction rights. `readonly` (a non-member — before they've
   * joined) hides the Reply link and disables reactions; `reaction-only`
   * (broadcast members) hides Reply but keeps reactions. Defaults to `normal`.
   */
  mode?: MessageInteractionMode;
}

/**
 * One channel message — avatar, name (+ optional "(You)"), timestamp,
 * rich body (Slate-DOM / markdown / plain-text), reaction chips +
 * picker, "View Replies" / "Reply" thread links.
 *
 * Deleted messages get a placeholder string rather than the body —
 * matches PopshopRN's "This message has been deleted." treatment.
 */
export function ChannelMessageBubble({
  message: seedMessage,
  currentUserId,
  channelSlug,
  hideThreadLinks,
  repliesMode = 'navigate',
  preserveWhenGone = false,
  mode = 'normal',
}: Props) {
  const { resendMessage } = useCommunityMessageContext();
  const { basePath } = useCommunityContext();
  // Inline-mode local state: whether the replies subtree is expanded and
  // whether the reply composer is showing. Unused in navigate mode.
  const [repliesExpanded, setRepliesExpanded] = useState(false);
  const [replying, setReplying] = useState(false);
  // Merge the message's own live Firestore doc over the seeded row so reaction
  // counts / reply counts / pin state / edits stay realtime on every message,
  // not just the newest ones the list subscription re-delivers.
  const message = useLiveMessage(seedMessage, preserveWhenGone);
  // Read once here (not in the kebab) so the header badge + kebab share a
  // single channel-bookmarks subscription per bubble.
  const bookmarkedIds = useMyMessageBookmarks(seedMessage.channelId);
  // Same for message-notification state — the member's subscribed-message ids
  // drive both the header bell and the kebab's Notifications label.
  const membership = useMyChannelMembership(seedMessage.channelId);
  if (!message) return null;

  const author = message.profile;
  const isMine = message.userId === currentUserId;
  const displayName = formatChannelAuthorName(author);
  const timestamp = formatMessageTimestamp(message.createdAt);
  const isPinned = message.pinnedAt != null;
  const isBookmarked = bookmarkedIds.has(message.id);
  const isSubscribed =
    membership?.subscribedToUpdatesOfMessages.includes(message.id) ?? false;

  const threadHref =
    channelSlug && !hideThreadLinks
      ? `${basePath}/${channelSlug}/thread/${message.id}`
      : undefined;

  // Footer: [reaction area] + [Reply / View Replies] on ONE wrapping row — they
  // stay on the same line when the bubble is wide enough and only wrap to
  // separate lines when it's too narrow (mobile / the thread pane). The reaction
  // area shows whenever there are existing chips (kept even on a DELETED message,
  // matching upstream) OR the viewer can still add a reaction (a live,
  // non-read-only message). Reply shows only for a top-level, un-replied, live
  // message in normal mode; once it has replies we show "View Replies" — a
  // deleted message keeps "Replies (N)" (no "View", not repliable) but loses the
  // Reply affordance.
  const hasReactions = Object.values(message.reactions).some(
    stat => (stat.count ?? 0) > 0,
  );
  const showReactionArea =
    hasReactions || (mode !== 'readonly' && !message.deleted);

  // Reply affordances (upstream `footer.tsx`): a message can be replied to up
  // to the model's 3-level depth. The "Replies (N)" toggle shows once it has
  // replies (kept for deleted messages — read-only). The standalone "Reply"
  // action is normal-mode + live only, and is hidden for a level-1 message that
  // already has replies (you reply from inside the expanded thread instead).
  const inline = repliesMode === 'inline';
  const canBeReplied = message.level <= 3;
  const showRepliesToggle = canBeReplied && message.replyCount > 0;
  const showReplyAction =
    canBeReplied &&
    mode === 'normal' &&
    !message.deleted &&
    (message.level > 1 || message.replyCount === 0);

  const navRepliesLink = !inline && threadHref != null && showRepliesToggle;
  const navReplyLink = !inline && threadHref != null && showReplyAction;
  const inlineRepliesToggle = inline && showRepliesToggle;
  const inlineReplyAction = inline && showReplyAction;

  const onReplyClick = () => {
    setReplying(true);
    setRepliesExpanded(true);
  };
  const onCollapseReplies = () => {
    setRepliesExpanded(false);
    setReplying(false);
  };
  const showInlineReplies = inline && (repliesExpanded || replying);

  const showStatus = message.isSending === true || message.sendingError != null;
  const showThreadArea =
    navRepliesLink ||
    navReplyLink ||
    inlineRepliesToggle ||
    inlineReplyAction ||
    showStatus;

  return (
    <article
      className={`min-w-0 shrink-0 overflow-hidden rounded-lg px-3 py-3 ${
        // Pinned messages get a warm-tinted background to stand out (upstream
        // uses a faded amber highlight); `stone` is the warm neutral scale.
        isPinned ? 'bg-surface-2' : 'bg-surface-1'
      }`}
    >
      {isPinned ? (
        <div className="text-brand-text/80 mb-1 flex items-center gap-1 text-[11px]">
          <Pin className="h-3 w-3 rotate-45" aria-hidden /> Pinned by{' '}
          {displayName}
        </div>
      ) : null}
      <div className="flex items-start gap-2.5">
        <MessageAuthorCard
          message={message}
          displayName={displayName}
          currentUserId={currentUserId}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="text-content-1 text-sm font-semibold">
              {displayName}
            </span>
            {isMine ? (
              <span className="text-content-4 text-xs">(You)</span>
            ) : null}
            <span className="text-content-4 text-xs">{timestamp}</span>
            <div className="ml-auto flex items-center gap-1.5">
              {isSubscribed ? (
                <MessageSubscriptionIndicator
                  channelId={message.channelId}
                  messageId={message.id}
                />
              ) : null}
              {isBookmarked ? (
                <MessageBookmarkIndicator
                  channelId={message.channelId}
                  messageId={message.id}
                />
              ) : null}
              {mode !== 'readonly' ? (
                <ChannelMessageMenu
                  message={message}
                  currentUserId={currentUserId}
                  isBookmarked={isBookmarked}
                  isSubscribed={isSubscribed}
                />
              ) : null}
            </div>
          </div>
          {message.deleted ? (
            <div className="border-line-strong bg-surface-1 text-content-3 mt-1.5 rounded-lg border px-4 py-3 text-sm">
              This message has been deleted.
            </div>
          ) : (
            <div className="text-content-1 mt-1.5">
              <MessageContentRenderer
                content={message.content.textContent.content}
                contentType={message.content.textContent.type}
              />
            </div>
          )}

          {!message.deleted ? (
            <div className="mt-3">
              <ChannelMessageAttachments message={message} />
            </div>
          ) : null}

          {!message.deleted && message.content.links?.length ? (
            <div className="mt-3">
              <ChannelMessageLinks
                links={message.content.links}
                isOwn={isMine}
              />
            </div>
          ) : null}

          {showReactionArea || showThreadArea ? (
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs">
              {showReactionArea ? (
                <MessageReactionBar
                  message={message}
                  currentUserId={currentUserId}
                  mode={mode}
                />
              ) : null}
              {showThreadArea ? (
                // Grouped so the link + "· last reply" wrap together as a unit,
                // dropping below the reactions only when there's no room beside
                // them.
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  {navRepliesLink ? (
                    <Link
                      href={threadHref!}
                      className="text-brand-text hover:text-brand-hover"
                    >
                      {message.deleted ? 'Replies' : 'View Replies'} (
                      {message.replyCount})
                    </Link>
                  ) : inlineRepliesToggle ? (
                    <button
                      type="button"
                      onClick={() => setRepliesExpanded(value => !value)}
                      className="text-brand-text hover:text-brand-hover"
                    >
                      {repliesExpanded
                        ? 'Hide Replies'
                        : message.deleted
                          ? 'Replies'
                          : 'View Replies'}{' '}
                      ({message.replyCount})
                    </button>
                  ) : null}
                  {navReplyLink ? (
                    <Link
                      href={threadHref!}
                      className="text-brand-text hover:text-brand-hover inline-flex items-center gap-1"
                    >
                      <CornerUpLeft className="h-3.5 w-3.5" aria-hidden /> Reply
                    </Link>
                  ) : inlineReplyAction ? (
                    <button
                      type="button"
                      onClick={onReplyClick}
                      className="text-brand-text hover:text-brand-hover inline-flex items-center gap-1"
                    >
                      <CornerUpLeft className="h-3.5 w-3.5" aria-hidden /> Reply
                    </button>
                  ) : null}
                  {(navRepliesLink || inlineRepliesToggle) &&
                  message.lastRepliedAt ? (
                    <span className="text-content-4">
                      · last reply{' '}
                      {formatChannelTimestamp(message.lastRepliedAt)}
                    </span>
                  ) : null}
                  {message.isSending ? (
                    <Loader2
                      aria-label="Sending"
                      className="text-content-3 h-3.5 w-3.5 animate-spin"
                    />
                  ) : null}
                  {message.sendingError ? (
                    <span className="flex items-center gap-2 text-red-400">
                      <span>Failed to send</span>
                      <button
                        type="button"
                        onClick={() => void resendMessage(message)}
                        className="text-brand-text hover:text-brand-hover inline-flex items-center gap-1"
                      >
                        <RotateCcw className="h-3.5 w-3.5" aria-hidden /> Retry
                      </button>
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}

          {showInlineReplies ? (
            <ChannelInlineReplies
              message={message}
              currentUserId={currentUserId}
              channelSlug={channelSlug}
              mode={mode}
              replying={replying}
              onCancelReply={() => setReplying(false)}
              onCollapse={onCollapseReplies}
            />
          ) : null}
        </div>
      </div>
    </article>
  );
}
